import { apiBase } from './apiBase';
export type RuntimeCfg = { apiBase?: string; apiKey?: string; authBearer?: string };
let RUNTIME: RuntimeCfg | null = null;

export function __resetApiClientForTests(): void {
  RUNTIME = null;
  csrfToken = readCsrf();
}

export async function loadRuntime(): Promise<RuntimeCfg> {
  if (RUNTIME) return RUNTIME;
  try {
    const r = await fetch(`/config.json`, { credentials: 'same-origin', cache: 'no-store' });
    RUNTIME = await r.json();
  } catch {
    RUNTIME = {};
  }
  return RUNTIME!;
}

function join(a: string, b: string, canonical = false) {
  if (canonical) {
    const endpoint = `/${b.replace(/^\/+/, '')}`;
    // Canonical endpoints are Flask root routes. Preserve a separately
    // configured backend origin, but do not carry its legacy transport prefix
    // (such as /api) into the root-route request. For a relative same-origin
    // base, Caddy routes these exact root paths to the backend.
    if (/^https?:\/\//i.test(a)) {
      return `${new URL(a).origin}${endpoint}`;
    }
    return endpoint;
  }
  const base = a.replace(/\/+$/, '');
  let path = b.replace(/^\/+/, '');
  // Callers that already have an /api-prefixed path must not turn an
  // /api-style configured base into /api/api. Canonical callers use endpoint
  // names (for example, /strategy_health), so this only removes a duplicate
  // transport prefix and never changes the endpoint name.
  if (/(?:^|\/)api$/.test(base) && /^api(?:\/|$)/.test(path)) {
    path = path.replace(/^api\/?/, '');
  }
  return `${base}/${path}`;
}

export async function apiFetch(path: string, init: RequestInit = {}, canonical = false) {
  const cfg = await loadRuntime();
  const base = cfg.apiBase ?? apiBase();
  const url = join(base, path, canonical);

  const headers = new Headers(init.headers);
  if (cfg.apiKey) headers.set('X-API-KEY', cfg.apiKey);
  if (cfg.authBearer && !headers.has('Authorization')) {
    headers.set('Authorization', `Bearer ${cfg.authBearer}`);
  }
  const opts: RequestInit = {
    ...init,
    headers,
    credentials: 'include',
  };

  const resp = await fetch(url, opts);
  if (resp.status === 401) {
    console.warn(`[api] 401 for ${url} — check X-API-KEY/Authorization and cookies`);
  }
  return resp;
}

export type ApiResult<T> = { ok: boolean; unauth?: boolean; data?: T; status?: string; httpStatus?: number };
export type FetchJsonOptions = RequestInit & { unwrapContract?: boolean; canonical?: boolean };
export type ApiWarning = {
  path: string;
  status?: number;
  message: string;
  snippet?: string;
  route?: string;
  requestId?: string;
};

type ApiWarningListener = (warning: ApiWarning) => void;

const apiWarningListeners = new Set<ApiWarningListener>();

export function subscribeApiWarnings(listener: ApiWarningListener): () => void {
  apiWarningListeners.add(listener);
  return () => apiWarningListeners.delete(listener);
}

function emitApiWarning(warning: ApiWarning): void {
  apiWarningListeners.forEach((listener) => listener(warning));
}

let csrfToken: string | null = null;

function readCsrf(): string | null {
  const m = document.cookie.match(/(?:^|;\s*)csrf_token=([^;]+)/);
  return m ? decodeURIComponent(m[1]) : null;
}

export function refreshCsrf(): void {
  csrfToken = readCsrf();
}

refreshCsrf();

function deriveStatus(payload: unknown): string | undefined {
  if (!payload || typeof payload !== 'object') return undefined;
  const data = payload as Record<string, unknown>;
  const candidates = [data.status, data.message, data.error];
  const status = candidates.find((v) => typeof v === 'string' && v.trim()) as string | undefined;
  return status?.trim();
}

function readContractPayload(payload: unknown): {
  isContract: boolean;
  data: unknown;
  ok?: boolean;
  route?: string;
  requestId?: string;
  error?: string;
} {
  if (!payload || typeof payload !== 'object') {
    return { isContract: false, data: payload };
  }
  const raw = payload as Record<string, unknown>;
  if (!('ok' in raw) || !('data' in raw)) {
    return { isContract: false, data: payload };
  }
  return {
    isContract: true,
    data: raw.data,
    ok: typeof raw.ok === 'boolean' ? raw.ok : undefined,
    route: typeof raw.route === 'string' ? raw.route : undefined,
    requestId: typeof raw.request_id === 'string' ? raw.request_id : undefined,
    error: typeof raw.error === 'string' ? raw.error : undefined,
  };
}

async function readPayload(
  res: Response,
): Promise<{ payload: unknown; text: string; jsonParsed: boolean }> {
  try {
    const text = await res.clone().text();
    if (!text) {
      return { payload: undefined, text: '', jsonParsed: false };
    }
    try {
      return { payload: JSON.parse(text), text, jsonParsed: true };
    } catch {
      return { payload: text, text, jsonParsed: false };
    }
  } catch {
    return { payload: undefined, text: '', jsonParsed: false };
  }
}

export async function fetchJson<T = unknown>(path: string, init: FetchJsonOptions = {}): Promise<ApiResult<T>> {
  const { unwrapContract = true, canonical = false, ...requestInit } = init;
  const opts: RequestInit = { ...requestInit };
  const method = (opts.method || 'GET').toUpperCase();
  const headers = new Headers(opts.headers || {});
  if (method !== 'GET' && csrfToken) {
    headers.set('X-CSRF-Token', csrfToken);
  }
  opts.headers = headers;
  opts.credentials = 'include';

  let res: Response;
  try {
    res = await apiFetch(path, opts, canonical);
  } catch (err) {
    console.warn('[api] network failure', err);
    return { ok: false, status: 'network_error' };
  }

  const { payload, jsonParsed } = await readPayload(res);
  const contentType = res.headers.get('content-type') || '';
  const expectsJson = contentType.includes('application/json');
  const payloadStatus = deriveStatus(payload);
  const contract = readContractPayload(payload);
  const normalizedPath = (() => {
    const origin =
      typeof window !== 'undefined' && window.location?.origin ? window.location.origin : 'http://localhost';
    try {
      const url = new URL(path, origin);
      return url.pathname.replace(/\/+$/, '') || '/';
    } catch {
      return path.split('?')[0].replace(/\/+$/, '') || '/';
    }
  })();

  const fallbackConfig = (() => {
    const heartbeatFallback = {
      data: {
        ok: false,
        status: 'error',
        reachable: false,
        latest: {},
        error: 'heartbeat_unavailable',
      },
      validator: (data: unknown) =>
        Boolean(data && typeof data === 'object' && typeof (data as Record<string, unknown>).status === 'string'),
    };
    const systemStatusFallback = {
      data: {
        overall_status: 'down',
        trading_mode: 'paper',
        safe_mode: false,
        shutdown_requested: false,
        modules: [],
      },
      validator: (data: unknown) =>
        Boolean(
          data &&
            typeof data === 'object' &&
            ('modules' in (data as Record<string, unknown>) || 'overall_status' in (data as Record<string, unknown>)),
        ),
    };
    const sessionFallback = {
      data: { ok: false },
      validator: (data: unknown) => Boolean(data && typeof data === 'object' && 'ok' in (data as Record<string, unknown>)),
    };
    const engineAllocationsFallback = {
      data: { allocations: {}, suggested: [] },
      validator: (data: unknown) =>
        Boolean(data && typeof data === 'object' && 'allocations' in (data as Record<string, unknown>)),
    };
    const shutdownFlagsFallback = {
      data: {
        flags: {
          signal_engine: false,
          scalping_engine: false,
          horizon_engine: false,
          nebula_horizon: false,
        },
      },
      validator: (data: unknown) =>
        Boolean(data && typeof data === 'object' && 'flags' in (data as Record<string, unknown>)),
    };

    if (normalizedPath === '/api/heartbeat' || normalizedPath === '/heartbeat') return heartbeatFallback;
    if (normalizedPath === '/api/system_status') return systemStatusFallback;
    if (normalizedPath === '/api/auth/session' || normalizedPath === '/auth/session') return sessionFallback;
    if (normalizedPath === '/api/engine_allocations' || normalizedPath === '/engine_allocations') {
      return engineAllocationsFallback;
    }
    if (normalizedPath === '/api/shutdown_flags' || normalizedPath === '/shutdown_flags') {
      return shutdownFlagsFallback;
    }
    if (normalizedPath === '/api/shutdown_status' || normalizedPath === '/shutdown_status') {
      return shutdownFlagsFallback;
    }
    return null;
  })();

  const reportApiIssue = (message: string) => {
    const route = contract.route || normalizedPath;
    const requestId = contract.requestId;
    const errorLabel = contract.error || message;
    const warningMessage = `${errorLabel} (route=${route}${requestId ? `, request_id=${requestId}` : ''})`;
    console.error('[api] invalid response', {
      path,
      status: res.status,
      contentType,
      message,
      body: 'invalid response body omitted',
      route,
      requestId,
    });
    emitApiWarning({
      path,
      status: res.status,
      message: warningMessage,
      route,
      requestId,
    });
  };

  if (res.status === 401 || res.status === 403) {
    return {
      ok: false,
      unauth: true,
      data: (unwrapContract ? contract.data : payload) as T,
      status: payloadStatus,
      httpStatus: res.status,
    };
  }

  if (!res.ok) {
    if (contract.isContract && expectsJson && jsonParsed) {
      return {
        ok: false,
        data: (unwrapContract ? (contract.data ?? fallbackConfig?.data ?? payload) : payload) as T,
        status: payloadStatus ?? contract.error ?? 'http_error',
        httpStatus: res.status,
      };
    }
    reportApiIssue('http_error');
    return {
      ok: false,
      data: (unwrapContract ? (contract.data ?? fallbackConfig?.data ?? payload) : payload) as T,
      status: payloadStatus ?? 'http_error',
      httpStatus: res.status,
    };
  }

  if (!expectsJson || !jsonParsed) {
    const message = expectsJson ? 'json_parse_failed' : 'content_type_mismatch';
    reportApiIssue(message);
    return {
      ok: false,
      data: (unwrapContract ? (fallbackConfig?.data ?? contract.data ?? payload) : payload) as T,
      status: payloadStatus ?? message,
      httpStatus: res.status,
    };
  }

  if (fallbackConfig && !fallbackConfig.validator(contract.data ?? payload)) {
    reportApiIssue('unexpected_shape');
    return {
      ok: false,
      data: (unwrapContract ? (fallbackConfig.data ?? contract.data ?? payload) : payload) as T,
      status: payloadStatus ?? 'invalid_shape',
      httpStatus: res.status,
    };
  }

  const unauthorizedPayload = payload as Record<string, unknown>;
  const unauthorized =
    payload &&
    typeof payload === 'object' &&
    (unauthorizedPayload.error === 'unauthorized' ||
      unauthorizedPayload.authorized === false ||
      unauthorizedPayload.unauthorized === true);

  if (unauthorized) {
    return {
      ok: false,
      unauth: true,
      data: (unwrapContract ? contract.data : payload) as T,
      status: payloadStatus,
      httpStatus: res.status,
    };
  }

  if (contract.isContract && contract.ok === false) {
    return {
      ok: false,
      data: (unwrapContract ? (contract.data ?? fallbackConfig?.data ?? payload) : payload) as T,
      status: payloadStatus ?? contract.error ?? 'error',
      httpStatus: res.status,
    };
  }

  return { ok: true, data: (unwrapContract ? (contract.data ?? payload) : payload) as T, status: payloadStatus, httpStatus: res.status };
}

function resolveAuthPath(path: string): string {
  if (/^https?:\/\//i.test(path)) {
    return path;
  }
  if (path.startsWith('/')) {
    return path;
  }
  if (path.startsWith('../')) {
    const normalized = path.replace(/^\.\.\/+/, '');
    return `/${normalized}`;
  }
  return `/${path}`;
}

export async function fetchAuthJson<T = unknown>(path: string, init: RequestInit = {}): Promise<ApiResult<T>> {
  const resolvedPath = resolveAuthPath(path);
  const opts: RequestInit = { ...init };
  const method = (opts.method || 'GET').toUpperCase();
  const headers = new Headers(opts.headers || {});
  if (method !== 'GET' && csrfToken) {
    headers.set('X-CSRF-Token', csrfToken);
  }
  opts.headers = headers;
  opts.credentials = 'include';
  try {
    const res = await fetch(resolvedPath, opts);
    if (res.status === 401 || res.status === 403) {
      return { ok: false, unauth: true };
    }
    if (!res.ok) {
      return { ok: false };
    }
    const payload = (await res.json()) as unknown;
    const contract = readContractPayload(payload);
    if (contract.isContract && contract.ok === false) {
      return { ok: false, data: contract.data as T };
    }
    return { ok: true, data: (contract.data ?? payload) as T };
  } catch {
    return { ok: false };
  }
}

type LoginResult = {
  ok: boolean;
  status?: number;
  data?: unknown;
  error?: string;
};

function extractErrorMessage(payload: unknown): string | undefined {
  if (!payload) return undefined;
  if (typeof payload === 'string') return payload.trim() || undefined;
  if (typeof payload === 'object') {
    const data = payload as Record<string, unknown>;
    const values = [data.error, data.message, data.detail];
    for (const value of values) {
      if (typeof value === 'string' && value.trim()) {
        return value.trim();
      }
    }
  }
  return undefined;
}

async function readLoginPayload(res: Response): Promise<unknown> {
  try {
    const text = await res.text();
    if (!text) return undefined;
    try {
      return JSON.parse(text);
    } catch {
      return text;
    }
  } catch {
    return undefined;
  }
}

export async function login(username: string, password: string): Promise<LoginResult> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (csrfToken) headers['X-CSRF-Token'] = csrfToken;
  const res = await fetch(apiBase('auth/login'), {
    method: 'POST',
    headers,
    body: JSON.stringify({ username, password }),
    credentials: 'same-origin',
  });
  const payload = await readLoginPayload(res);
  if (res.ok) {
    refreshCsrf();
    return { ok: true, data: payload };
  }
  return {
    ok: false,
    status: res.status,
    data: payload,
    error: extractErrorMessage(payload),
  };
}

export type SystemStatusModule = {
  name: string;
  display_name?: string;
  status: 'ok' | 'stale' | 'missing' | 'error';
  healthy: boolean;
  last_heartbeat_ts?: string | null;
  age_seconds?: number | null;
  reason?: string | null;
  // legacy fields kept for compatibility with earlier payloads
  id?: string;
  label?: string;
  last_heartbeat?: string | null;
  details?: string | null;
};

export type SystemStatus = {
  overall_status: 'ok' | 'degraded' | 'down';
  trading_mode: 'paper' | 'live' | 'dry_run' | 'disabled' | string;
  safe_mode: boolean;
  shutdown_requested: boolean;
  last_updated?: string;
  mode?: {
    trading_mode: 'paper' | 'live' | 'dry_run' | string;
    safe_mode: boolean;
    shutdown_blocked: boolean;
  };
  modules: SystemStatusModule[];
  system_metrics?: { cpu_percent?: number | null; memory_percent?: number | null; disk_usage_percent?: number | null } | null;
  metrics?: { cpu_percent?: number | null; mem_percent?: number | null; memory_percent?: number | null } | null;
};

export async function getSystemStatus(): Promise<ApiResult<SystemStatus>> {
  try {
    return await fetchJson<SystemStatus>(apiBase('system_status'));
  } catch (err) {
    console.warn('[api] system status fetch failed', err);
    return { ok: false };
  }
}

export type EngineAllocations = Record<string, Record<string, number>>;
export type EngineAllocationsPayload = { allocations: EngineAllocations; suggested?: unknown[] };

export async function getEngineAllocations(): Promise<ApiResult<EngineAllocationsPayload>> {
  return fetchJson<EngineAllocationsPayload>(apiBase('engine_allocations'));
}

export type AdaptiveWeightEngine = { status?: string; weight?: number | null };
export type AdaptiveWeightsCoin = { coin: string; engines: Record<string, AdaptiveWeightEngine> };
export type AdaptiveWeightsSummary = {
  ok?: boolean;
  coins?: AdaptiveWeightsCoin[];
  count?: number;
  ts?: string;
  message?: string;
  error?: string;
};

function adaptiveStatusFromPayload(data?: AdaptiveWeightsSummary): string | undefined {
  const coins = Array.isArray(data?.coins) ? data?.coins : [];
  if (coins.length === 0) return 'no_data_yet';
  const hasCalibrated = coins.some((coin) =>
    Object.values(coin?.engines || {}).some((engine) => engine?.status === 'calibrated'),
  );
  return hasCalibrated ? undefined : 'no_data_yet';
}

export async function getAdaptiveWeightsSummary(): Promise<ApiResult<AdaptiveWeightsSummary>> {
  const res = await fetchJson<AdaptiveWeightsSummary>(apiBase('adaptive_weights/summary'));
  if (!res.ok) return res;
  const status = res.status ?? adaptiveStatusFromPayload(res.data);
  return { ...res, status };
}
